import { useEffect, useRef } from "react";
import type { PropsWithChildren } from "react";
import clsx from "clsx";
import { gsap, ScrollTrigger, ensureGsapRegistered } from "../../lib/gsap";
import { prefersReducedMotion } from "../../lib/deviceCapability";

export interface RevealOnScrollProps {
  className?: string;
  delay?: number;
  y?: number;
  scale?: number;
}

export function RevealOnScroll({
  children,
  className,
  delay = 0,
  y = 32,
  scale = 0.97
}: PropsWithChildren<RevealOnScrollProps>) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (prefersReducedMotion()) {
      gsap.set(el, { opacity: 1, y: 0, scale: 1 });
      return;
    }

    ensureGsapRegistered();
    gsap.set(el, { opacity: 0, y, scale });

    const tween = gsap.to(el, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.9,
      delay,
      ease: "power3.out",
      scrollTrigger: {
        trigger: el,
        start: "top 88%",
        toggleActions: "play none none none"
      }
    });

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [delay, y, scale]);

  return (
    <div ref={ref} className={clsx(className)}>
      {children}
    </div>
  );
}

export function useParallax(ref: React.RefObject<HTMLElement>, speed = 0.3) {
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;

    ensureGsapRegistered();
    const tween = gsap.to(el, {
      yPercent: speed * 100,
      ease: "none",
      scrollTrigger: {
        trigger: el.parentElement ?? el,
        start: "top bottom",
        end: "bottom top",
        scrub: true
      }
    });

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [ref, speed]);
}
